/**
 * RemoteTransform3D's `get_configuration_warnings()` (remote_transform_3d.cpp:206-211): it warns
 * unless `remote_node` (key `remote_path`) resolves to a Node3D. `remote_node` defaults to
 * `NodePath()` (remote_transform_3d.h:38, no constructor override), which triggers it, so the rule
 * covers an absent path, a path to nothing in this file and a path to a node that is no Node3D.
 * A second arm warns on a relay in a relay cycle (`relayCycles.ts`).
 */
import { ruleRegistry } from '../../../linter/RuleRegistry.js';
import { descendsFromClass } from '../../../godot/classdb/classBaseTypes.js';
import { extractNodePath } from '../../../linter/linterUtils.js';
import { resolveNodePath } from '../../../linter/nodePathResolve.js';
import { armDiagnostic, armEmits, groundedArm } from '../../../linter/ruleArms.js';
import { relayCycleArm, relayCycleDiagnostics } from '../../shared/remotetransform/relayCycles.js';
const arms = {
    invalidRemotePath: groundedArm('remotetransform3d-invalid-remote-path', { kind: 'configuration-warning' }),
    relayCycle: relayCycleArm('remotetransform3d-relay-cycle', 'remote_transform_3d.cpp:118-126'),
};
function checkRemoteTransform3D(context) {
    const { node, scene } = context;
    const properties = node.rawProperties;
    const path = properties.remote_path ? extractNodePath(properties.remote_path) : null;
    // Absent key, or an explicit empty NodePath(""): both are `NodePath()`, the default and the trigger.
    if (!path) {
        return [
            armDiagnostic(arms.invalidRemotePath, node, `RemoteTransform3D '${node.name}' has no Remote Path set. The Remote Path property must point to a valid Node3D or Node3D-derived node to work.`),
        ];
    }
    // Silent only on `unknowable`: an instanced ancestor, an instanced target or a walk into content
    // another file declares. A `..` segment climbs, as `get_node_or_null` does.
    const target = resolveNodePath(scene, node, path);
    if (target.status === 'missing') {
        return [
            armDiagnostic(arms.invalidRemotePath, node, `RemoteTransform3D '${node.name}' has remote_path set to NodePath("${path}"), which names no node in this file. The Remote Path property must point to a valid Node3D or Node3D-derived node to work.`),
        ];
    }
    // `descendsFromClass` is reflexive (classBaseTypes.ts), so it also answers the exact-match case.
    if (target.status === 'found' && !descendsFromClass(target.node.type, 'Node3D')) {
        return [
            armDiagnostic(arms.invalidRemotePath, node, `RemoteTransform3D '${node.name}' has remote_path pointing to a ${target.node.type} node, but the Remote Path property must point to a valid Node3D or Node3D-derived node to work.`),
        ];
    }
    return [];
}
const remoteTransform3DValidationRule = {
    meta: {
        name: 'valid-remotetransform3d-remote-path',
        description: "Mirrors RemoteTransform3D::get_configuration_warnings' remote_path check: absent, dangling, or not a Node3D. Warns on a relay in a relay cycle",
        category: 'validation',
        applicableNodeTypes: ['RemoteTransform3D'],
        emits: armEmits(arms),
    },
    check: (context) => [
        ...checkRemoteTransform3D(context),
        ...relayCycleDiagnostics(arms.relayCycle, context),
    ],
};
ruleRegistry.register(remoteTransform3DValidationRule);
//# sourceMappingURL=linter.js.map